import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Users, Clock, Check, MapPin, Mail, Phone, Building2, Calendar, MessageSquare, Tag, Globe, BarChart3, Archive } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { getImageUrl } from '../utils/imageUrl';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AreaManagerDashboard({ user }) {
  const [stats, setStats] = useState(null);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [members, setMembers] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('members');

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, pendingRes, membersRes, eventsRes] = await Promise.all([
        axios.get(`${API}/area-manager/stats`),
        axios.get(`${API}/area-manager/pending-users`),
        axios.get(`${API}/area-manager/members`),
        axios.get(`${API}/area-manager/events`)
      ]);
      setStats(statsRes.data);
      setPendingUsers(pendingRes.data);
      setMembers(membersRes.data);
      setEvents(eventsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleArchiveMember = async (memberId, memberName, archived) => {
    const action = archived ? 'archive' : 'reactivate';
    if (!window.confirm(`Are you sure you want to ${action} ${memberName}?${archived ? ' They will not be able to log in.' : ''}`)) return;
    try {
      await axios.post(`${API}/chapters/archive-member`, { user_id: memberId, archived });
      toast.success(`${memberName} ${archived ? 'archived' : 'reactivated'} successfully`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${action} member`);
    }
  };

  const handleApprove = async (userId) => {
    try {
      await axios.post(`${API}/area-manager/approve-user/${userId}`);
      toast.success('User approved!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve user');
    }
  };

  if (loading) return <div className="text-center py-12">Loading...</div>;

  return (
    <div className="max-w-7xl mx-auto px-6" data-testid="area-manager-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <h1 className="text-4xl font-bold text-[#2d3748]">My Chapter</h1>
          <span className="px-3 py-1 bg-[#cb9944] text-white text-sm rounded-full">
            {stats?.area || 'All Areas'}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/group-chats">
            <Button className="bg-[#5b7f95] hover:bg-[#4a6b80] text-white rounded-full" data-testid="chapter-group-chat-btn">
              <MessageSquare className="w-4 h-4 mr-2" />
              Chapter Group Chat
            </Button>
          </Link>
          <Link to="/reports">
            <Button className="bg-[#cb9944] hover:bg-[#b08435] text-white rounded-full" data-testid="chapter-reports-btn">
              <BarChart3 className="w-4 h-4 mr-2" />
              Reports
            </Button>
          </Link>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card className="border-gray-100 rounded-2xl shadow-sm">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#64748b] mb-1">Total Members</p>
                  <p className="text-3xl font-bold text-[#2d3748]">{stats.total_members}</p>
                </div>
                <div className="w-12 h-12 bg-[#5b7f95]/10 rounded-full flex items-center justify-center">
                  <Users className="w-6 h-6 text-[#5b7f95]" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-gray-100 rounded-2xl shadow-sm">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#64748b] mb-1">Pending Approval</p>
                  <p className="text-3xl font-bold text-[#cb9944]">{stats.pending_members}</p>
                </div>
                <div className="w-12 h-12 bg-[#cb9944]/10 rounded-full flex items-center justify-center">
                  <Clock className="w-6 h-6 text-[#cb9944]" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-gray-100 rounded-2xl shadow-sm">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#64748b] mb-1">Chapter Events</p>
                  <p className="text-3xl font-bold text-[#5b7f95]">{events.length}</p>
                </div>
                <div className="w-12 h-12 bg-[#5b7f95]/10 rounded-full flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-[#5b7f95]" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-gray-100 rounded-2xl shadow-sm">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#64748b] mb-1">Total Bookings</p>
                  <p className="text-3xl font-bold text-green-600">
                    {events.reduce((sum, e) => sum + (e.attendees?.length || 0), 0)}
                  </p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <Check className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="flex gap-2 mb-6 flex-wrap">
        <Button
          variant={activeTab === 'members' ? 'default' : 'outline'}
          onClick={() => setActiveTab('members')}
          className={`rounded-full ${activeTab === 'members' ? 'bg-[#5b7f95]' : ''}`}
          data-testid="tab-members"
        >
          <Users className="w-4 h-4 mr-2" />
          Members ({members.length})
        </Button>
        <Button
          variant={activeTab === 'pending' ? 'default' : 'outline'}
          onClick={() => setActiveTab('pending')}
          className={`rounded-full ${activeTab === 'pending' ? 'bg-[#cb9944]' : ''}`}
          data-testid="tab-pending"
        >
          <Clock className="w-4 h-4 mr-2" />
          Pending ({pendingUsers.length})
        </Button>
        <Button
          variant={activeTab === 'events' ? 'default' : 'outline'}
          onClick={() => setActiveTab('events')}
          className={`rounded-full ${activeTab === 'events' ? 'bg-[#5b7f95]' : ''}`}
          data-testid="tab-events"
        >
          <Calendar className="w-4 h-4 mr-2" />
          Events ({events.length})
        </Button>
      </div>

      {/* Members Tab */}
      {activeTab === 'members' && (
        <Card className="border-gray-100 rounded-2xl shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-[#5b7f95]" />
              Members in {stats?.area}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {members.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {members.map((member) => (
                  <div
                    key={member.id}
                    className={`p-4 border rounded-xl hover:shadow-sm transition-shadow ${member.is_archived ? 'border-red-200 bg-red-50/30' : 'border-gray-100'}`}
                    data-testid="member-card"
                  >
                    <div className="flex items-start gap-4">
                      <Avatar className="h-12 w-12">
                        <AvatarImage src={member.profile_image ? getImageUrl(member.profile_image) : undefined} />
                        <AvatarFallback className={`${member.is_archived ? 'bg-gray-400' : 'bg-[#5b7f95]'} text-white`}>
                          {(member.display_name || member.full_name).charAt(0)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-[#2d3748] truncate">{member.display_name || member.full_name}</h3>
                          {member.is_archived && (
                            <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full font-medium">Archived</span>
                          )}
                        </div>
                        {member.business_name && (
                          <p className="text-sm text-[#5b7f95] truncate">{member.business_name}</p>
                        )}
                        {member.business_category && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#5b7f95]/10 text-[#5b7f95] text-xs font-medium rounded-full mt-1">
                            <Tag className="w-3 h-3" />
                            {member.business_category}
                          </span>
                        )}
                        <div className="space-y-1 mt-2">
                          <p className="text-xs text-[#64748b] flex items-center gap-1 truncate">
                            <Mail className="w-3 h-3 flex-shrink-0" />
                            {member.display_email || member.email}
                          </p>
                          {member.phone && (
                            <p className="text-xs text-[#64748b] flex items-center gap-1">
                              <Phone className="w-3 h-3 flex-shrink-0" />
                              {member.phone}
                            </p>
                          )}
                          {member.website && (
                            <p className="text-xs text-[#64748b] flex items-center gap-1 truncate">
                              <Globe className="w-3 h-3 flex-shrink-0" />
                              {member.website}
                            </p>
                          )}
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleArchiveMember(member.id, member.display_name || member.full_name, !member.is_archived)}
                          className={`mt-2 rounded-full text-xs ${member.is_archived ? 'text-green-600 hover:text-green-700 hover:bg-green-50' : 'text-red-500 hover:text-red-600 hover:bg-red-50'}`}
                          data-testid="archive-member-btn"
                        >
                          <Archive className="w-3 h-3 mr-1" />
                          {member.is_archived ? 'Reactivate' : 'Archive'}
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-[#64748b]">
                <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>No members in your chapter yet</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Pending Approvals Tab */}
      {activeTab === 'pending' && (
        <Card className="border-gray-100 rounded-2xl shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-[#cb9944]" />
              Pending Applications
            </CardTitle>
          </CardHeader>
          <CardContent>
            {pendingUsers.length > 0 ? (
              <div className="space-y-4">
                {pendingUsers.map((pendingUser) => (
                  <div
                    key={pendingUser.id}
                    className="flex items-start justify-between p-4 border border-gray-100 rounded-xl hover:shadow-sm transition-shadow"
                    data-testid="pending-user-card"
                  >
                    <div className="flex items-start gap-4 flex-1">
                      <Avatar className="h-12 w-12">
                        <AvatarFallback className="bg-[#cb9944] text-white">
                          {pendingUser.full_name.charAt(0)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <h3 className="font-semibold text-[#2d3748]">{pendingUser.full_name}</h3>
                        <p className="text-sm text-[#64748b]">{pendingUser.email}</p>
                        <p className="text-sm text-[#5b7f95] mt-1 flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {pendingUser.area}
                        </p>
                        {pendingUser.business_name && (
                          <div className="mt-2 p-2 bg-gray-50 rounded-lg">
                            <p className="text-sm font-semibold text-[#2d3748]">{pendingUser.business_name}</p>
                            {pendingUser.business_category && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#5b7f95]/10 text-[#5b7f95] text-xs font-medium rounded-full mt-1">
                                <Tag className="w-3 h-3" />
                                {pendingUser.business_category}
                              </span>
                            )}
                            {pendingUser.business_description && (
                              <p className="text-sm text-[#64748b] mt-1 line-clamp-2">{pendingUser.business_description}</p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2 ml-4">
                      <Button
                        onClick={() => handleApprove(pendingUser.id)}
                        size="sm"
                        className="bg-green-500 hover:bg-green-600 text-white rounded-full"
                        data-testid="approve-user-btn"
                      >
                        <Check className="w-4 h-4 mr-1" />
                        Approve
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-[#64748b]">
                <Check className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>No pending applications</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Events Tab */}
      {activeTab === 'events' && (
        <Card className="border-gray-100 rounded-2xl shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-[#5b7f95]" />
              Events in {stats?.area}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {events.length > 0 ? (
              <div className="space-y-4">
                {events.map((event) => (
                  <div
                    key={event.id}
                    className="p-4 border border-gray-100 rounded-xl hover:shadow-sm transition-shadow"
                    data-testid="event-card"
                  >
                    <div className="flex items-start gap-4">
                      {event.image && (
                        <img 
                          src={getImageUrl(event.image)} 
                          alt={event.title}
                          className="w-20 h-20 object-cover rounded-lg flex-shrink-0"
                        />
                      )}
                      <div className="flex-1">
                        <div className="flex items-start justify-between">
                          <div>
                            <h3 className="font-semibold text-[#2d3748]">{event.title}</h3>
                            <p className="text-sm text-[#64748b] flex items-center gap-1 mt-1">
                              <Calendar className="w-3 h-3" />
                              {format(new Date(event.date), 'PPP')}
                            </p>
                            {event.location && (
                              <p className="text-sm text-[#64748b] flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                {event.location}
                              </p>
                            )}
                          </div>
                          <div className="text-right">
                            {event.price > 0 && (
                              <span className="px-2 py-1 bg-[#cb9944]/10 text-[#cb9944] text-sm font-semibold rounded-full">
                                R{event.price}
                              </span>
                            )}
                            <div className="mt-2">
                              <span className="px-2 py-1 bg-green-100 text-green-700 text-sm font-medium rounded-full">
                                {event.attendees?.length || 0} bookings
                              </span>
                            </div>
                          </div>
                        </div>
                        
                        {/* Attendee list */}
                        {event.attendee_details && event.attendee_details.length > 0 && (
                          <div className="mt-4 pt-4 border-t border-gray-100">
                            <p className="text-sm font-medium text-[#2d3748] mb-2">Attendees:</p>
                            <div className="flex flex-wrap gap-2">
                              {event.attendee_details.map((attendee) => (
                                <div key={attendee.id} className="flex items-center gap-2 px-2 py-1 bg-gray-50 rounded-full">
                                  <Avatar className="h-6 w-6">
                                    <AvatarImage src={attendee.profile_image ? getImageUrl(attendee.profile_image) : undefined} />
                                    <AvatarFallback className="bg-[#5b7f95] text-white text-xs">
                                      {(attendee.display_name || attendee.full_name).charAt(0)}
                                    </AvatarFallback>
                                  </Avatar>
                                  <span className="text-xs text-[#2d3748]">{attendee.display_name || attendee.full_name}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-[#64748b]">
                <Calendar className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>No events in your chapter yet</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
